import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuthContext } from './AuthContext';
import BillingService, { PlanTier, BillingState } from '../features/billing/services/BillingService';

interface BillingContextValue {
  hasSubscription: boolean;
  activeAddons: string[];
  currentTier: PlanTier | null;
  loading: boolean;
  hasAddon: (slug: string) => boolean;
  refresh: () => void;
  billingState: BillingState;
  isDesignPartner: boolean;
  trialDaysRemaining: number;
  trialEnd: string | null;
}

const defaultValue: BillingContextValue = {
  hasSubscription: true,
  activeAddons: [],
  currentTier: null,
  loading: true,
  hasAddon: () => true,
  refresh: () => {},
  billingState: 'none',
  isDesignPartner: false,
  trialDaysRemaining: 0,
  trialEnd: null,
};

const BillingContext = createContext<BillingContextValue>(defaultValue);

export function BillingProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, selectedTenant } = useAuthContext();
  const [hasSubscription, setHasSubscription] = useState(false);
  const [activeAddons, setActiveAddons] = useState<string[]>([]);
  const [currentTier, setCurrentTier] = useState<PlanTier | null>(null);
  const [loading, setLoading] = useState(true);
  const [billingAvailable, setBillingAvailable] = useState(false);
  const [billingState, setBillingState] = useState<BillingState>('none');
  const [isDesignPartner, setIsDesignPartner] = useState(false);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(0);
  const [trialEnd, setTrialEnd] = useState<string | null>(null);

  const fetchBilling = useCallback(async () => {
    if (!isAuthenticated || !selectedTenant) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const summary = await BillingService.getSummary();
      setBillingAvailable(true);
      setHasSubscription(summary.has_subscription);
      setActiveAddons(summary.addons ?? []);
      setCurrentTier(summary.subscription?.plan?.tier ?? null);
      setBillingState(summary.billing_state ?? 'none');
      setIsDesignPartner(summary.is_design_partner ?? false);
      setTrialDaysRemaining(summary.trial_days_remaining ?? 0);
      setTrialEnd(summary.subscription?.trial_end ?? null);
    } catch {
      // Fail-open: if billing API is unavailable, grant full access
      setBillingAvailable(false);
      setHasSubscription(true);
      setActiveAddons([]);
      setCurrentTier(null);
      setBillingState('none');
      setIsDesignPartner(false);
      setTrialDaysRemaining(0);
      setTrialEnd(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, selectedTenant]);

  useEffect(() => {
    fetchBilling();
  }, [fetchBilling]);

  const hasAddon = useCallback(
    (slug: string) => !billingAvailable || !hasSubscription || activeAddons.includes(slug),
    [billingAvailable, hasSubscription, activeAddons],
  );

  const value: BillingContextValue = {
    hasSubscription,
    activeAddons,
    currentTier,
    loading,
    hasAddon,
    refresh: fetchBilling,
    billingState,
    isDesignPartner,
    trialDaysRemaining,
    trialEnd,
  };

  return (
    <BillingContext.Provider value={value}>
      {children}
    </BillingContext.Provider>
  );
}

export function useBilling(): BillingContextValue {
  return useContext(BillingContext);
}
